import {
  Flex,
  theme,
  Dropdown,
  Space,
  Switch,
  Typography,
  Grid,
  Button,
} from "antd";
import type { MenuProps } from "antd";
import { Header } from "antd/es/layout/layout";
import { useAppContext } from "../../context/app/context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserTie } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { LANGUAGES, LOCAL_STORAGE_KEYS } from "../../common/constants";
import { useThemeContext } from "../../context/theme/context";
import { setLanguage, setTheme } from "../../context/theme/action";
import { useNavigate } from "react-router-dom";
import {
  useGetMyPartnerQuery,
  useGetMyUserQuery,
  useLogoutMutation,
} from "../../graphql/generated/hooks";
import { MenuOutlined } from "@ant-design/icons";
import { useApolloClient } from "@apollo/client";
import { clearUser } from "../../context/app/action";

const { Link } = Typography;
const { useBreakpoint } = Grid;

export function RobinHeader({ onMenuClick }: { onMenuClick?: () => void }) {
  const { lg } = useBreakpoint();
  const { state, dispatch } = useAppContext();
  const themeContext = useThemeContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  // Only fetch partner data if we have both token AND authenticated user
  // This prevents premature queries before token validation completes
  const hasToken = !!localStorage.getItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
  const isAuthenticated = !!state.user?.partnerId;
  const { data } = useGetMyPartnerQuery({
    skip: !hasToken || !isAuthenticated,
  });
  const { data: myUserData } = useGetMyUserQuery({
    skip: !hasToken || !isAuthenticated,
  });

  const displayName =
    myUserData?.getMyUser?.name ||
    state.user?.username ||
    myUserData?.getMyUser?.email ||
    "";

  const toggleTheme = () => {
    themeContext.dispatch(
      setTheme(themeContext.state.mode === "light" ? "dark" : "light")
    );
  };

  const [logout] = useLogoutMutation();

  const languageToggle = (
    <Link
      onClick={() =>
        themeContext.dispatch(
          setLanguage(
            themeContext.state.language === LANGUAGES.EN
              ? LANGUAGES.AR
              : LANGUAGES.EN
          )
        )
      }
    >
      {themeContext.state.language === LANGUAGES.EN ? "العربية" : "English"}
    </Link>
  );

  const themeToggle = (
    <Switch
      checked={themeContext.state.mode === "dark"}
      onChange={toggleTheme}
      checkedChildren={t("theme.dark")}
      unCheckedChildren={t("theme.light")}
    />
  );

  const items: MenuProps["items"] = [
    ...(!lg
      ? [
          {
            label: displayName,
            disabled: true,
            key: "0",
          },
        ]
      : []),
    {
      label: (
        <Link
          onClick={() => {
            navigate("/profile");
          }}
        >
          {t("button.profile")}
        </Link>
      ),
      key: "1",
    },
    ...(!lg
      ? [
          {
            label: languageToggle,
            key: "2",
          },
          {
            label: themeToggle,
            key: "3",
          },
        ]
      : []),
    {
      type: "divider",
      key: "4",
    },
    {
      style: { color: "red" },
      label: t("button.logout"),
      key: "5",
      onClick: () => {
        const refreshToken = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
        const finalizeLogout = async () => {
          // Clear in-memory auth state immediately (prevents stale user showing until refresh)
          dispatch(clearUser());
          // Clear any cached "me/partner" data from Apollo
          try {
            await apolloClient.clearStore();
          } catch {
            // ignore cache clearing errors
          }
          localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
          localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
          navigate("/login", { replace: true });
        };
        if (refreshToken) {
          logout({ variables: { refreshToken } })
            .catch(() => {
              // Logout error handled silently
            })
            .finally(() => void finalizeLogout());
        } else {
          void finalizeLogout();
        }
      },
    },
  ];

  const largeActions = (
    <Space size="large">
      {themeToggle}
      {languageToggle}
      <Dropdown menu={{ items }}>
        <Link onClick={(e) => e.preventDefault()}>
          <Space>
            {displayName}
            <FontAwesomeIcon icon={faUserTie} />
          </Space>
        </Link>
      </Dropdown>
    </Space>
  );

  // Mobile/Tablet actions - only shown when !lg (below 992px breakpoint)
  const smallActions = (
    <Dropdown menu={{ items }} trigger={["click"]}>
      <Button
        type="text"
        icon={<MenuOutlined />}
        style={{
          fontSize: "18px",
          width: 40,
          height: 40,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          // Blue color - only applies to mobile/tablet since smallActions is only rendered when !lg
          color: "#33AAF2",
        }}
      />
    </Dropdown>
  );

  return (
    <Header
      style={{
        background: colorBgContainer,
        position: "sticky",
        top: 0,
        zIndex: 100,
        width: "100%",
        overflow: "hidden",
        padding: "0 16px",
        height: 64,
        lineHeight: "64px",
      }}
    >
      <Flex
        align="center"
        justify="space-between"
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        {/* Left side: Menu button (mobile/tablet) + Title */}
        <Flex align="center" gap={16} style={{ flex: 1, minWidth: 0 }}>
          {onMenuClick && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={onMenuClick}
              style={{
                fontSize: "18px",
                width: 40,
                height: 40,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            />
          )}
          <Typography.Title
            ellipsis
            level={3}
            style={{
              margin: 0,
              flex: onMenuClick ? 1 : "none",
              minWidth: 0,
              lineHeight: "1.2",
              display: "flex",
              alignItems: "center",
            }}
          >
            {data?.getPartner.name}
          </Typography.Title>
        </Flex>

        {/* Right side: Actions */}
        <Flex
          align="center"
          justify="flex-end"
          gap={16}
          style={{
            flexShrink: 0,
            marginLeft: 16,
          }}
        >
          {lg ? largeActions : smallActions}
        </Flex>
      </Flex>
    </Header>
  );
}
